import type { ApiClient } from '@carbonwebmail/api-client'
import type { CurrentSession } from '@carbonwebmail/contracts'
import { useAuth } from '@clerk/react'
import { useCallback, useEffect, useState } from 'react'

/**
 * Who is signed in, and whether they have a place in this product.
 *
 * Two questions with two answerers, which is the shape of the whole migration.
 * The identity provider says whether somebody is signed in — the app asks it
 * directly, in the browser, with no round trip. Only this product can say which
 * organisation they belong to, so that is one request to the server.
 *
 * **`needs-organisation` is a real state, not an error.** Somebody who has
 * just created an identity has no tenant until they name one, and the server
 * answers that with the same 401 it gives a stranger — deliberately, because
 * saying otherwise would tell an anonymous caller which subjects exist. This
 * hook is the one place that can tell them apart, because it is the one place
 * that also knows the provider's answer.
 *
 * `loading` is a real state too. Rendering the sign-in screen while either
 * answer is in flight would bounce a signed-in person to a login form for a
 * moment on every refresh, which reads as being logged out.
 */

export type SessionState =
  | { readonly status: 'loading' }
  | { readonly status: 'signed-in'; readonly session: CurrentSession }
  | { readonly status: 'needs-organisation' }
  | { readonly status: 'signed-out' }

export function useSession(client: ApiClient) {
  const { isLoaded, isSignedIn, signOut } = useAuth()
  const [state, setState] = useState<SessionState>({ status: 'loading' })

  const refresh = useCallback(async () => {
    if (!isLoaded) {
      setState({ status: 'loading' })
      return
    }

    if (!isSignedIn) {
      setState({ status: 'signed-out' })
      return
    }

    try {
      const session = await client.currentSession()
      setState(
        session === undefined ? { status: 'needs-organisation' } : { status: 'signed-in', session },
      )
    } catch {
      /*
       * An unreachable server is not the same as having no organisation, and
       * this is the one place the difference is invisible: `currentSession`
       * answers undefined for a 401 and throws for a dead socket.
       *
       * Signed out is the wrong answer — the provider has already said this
       * person is signed in, and showing them a sign-in screen they are
       * already past is a loop. Offering the organisation form is the right
       * one: it is the only thing on screen with a button, and submitting it
       * tries the server again.
       */
      setState({ status: 'needs-organisation' })
    }
  }, [client, isLoaded, isSignedIn])

  useEffect(() => {
    void refresh()
  }, [refresh])

  return { state, refresh, signOut: () => signOut() }
}
